import { useParcelExtractionEnabled } from '@/hooks/useParcelExtractionEnabled'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { FileText, Gauge, Wrench, Camera, CreditCard } from 'lucide-react'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useAuth } from '@/hooks/useAuth'
import { useUIStore } from '@/stores'

export default function QuickActionBar() {
  const { t } = useTranslation('common')
  const { user } = useAuth()
  const { data: accountSettings } = useAccountSettings()
  const isCondominium = accountSettings?.account_type === 'condominium'
  const mobileSidebarOpen = useUIStore((s) => s.mobileSidebarOpen)
  const parcelTextExtractionEnabled = useParcelExtractionEnabled()

  // EP-80: the quick actions are operational data-entry (invoice / meter /
  // maintenance / parcel) — owner + admin don't need them, so the bottom nav
  // is staff-only.
  if (user?.role !== 'staff') return null
  if (mobileSidebarOpen) return null

  return (
    // z-[60]: above sidebar drawer z-50, below toasts z-100
    // pb-[env(safe-area-inset-bottom)]: clears iPhone home indicator (viewport-fit=cover in index.html)
    <nav
      aria-label="Quick actions"
      className="fixed bottom-0 left-0 right-0 lg:hidden z-[60] bg-base-100 border-t border-base-200 shadow-lg pb-[env(safe-area-inset-bottom)]"
      data-testid="quick-action-bar"
    >
      <div className="flex">
        {/* P005: Record Payment — navigates to quick-pay form (standalone payment, no invoice required). */}
        <Link
          to="/payments/record"
          className="flex-1 basis-0 min-w-0 flex flex-col items-center justify-center gap-1 py-3 px-1 text-xs font-medium text-base-content/70 hover:text-primary hover:bg-base-200 transition-colors"
          data-testid="quick-action-payment"
        >
          <CreditCard className="w-5 h-5" />
          <span className="max-w-full truncate">{t('dashboard.quick_actions.bar.record_payment')}</span>
        </Link>

        <Link
          to="/invoices/new"
          className="flex-1 basis-0 min-w-0 flex flex-col items-center justify-center gap-1 py-3 px-1 text-xs font-medium text-base-content/70 hover:text-primary hover:bg-base-200 transition-colors"
          data-testid="quick-action-invoice"
        >
          <FileText className="w-5 h-5" />
          <span className="max-w-full truncate">{t('dashboard.quick_actions.bar.new_invoice')}</span>
        </Link>

        {/* Add Reading is hidden for condominium accounts — meter writes return 403 (convention #34) */}
        {!isCondominium && (
          <Link
            to="/meter-readings"
            className="flex-1 basis-0 min-w-0 flex flex-col items-center justify-center gap-1 py-3 px-1 text-xs font-medium text-base-content/70 hover:text-primary hover:bg-base-200 transition-colors"
            data-testid="quick-action-reading"
          >
            <Gauge className="w-5 h-5" />
            <span className="max-w-full truncate">{t('dashboard.quick_actions.bar.add_reading')}</span>
          </Link>
        )}

        <Link
          to="/maintenances/new"
          className="flex-1 basis-0 min-w-0 flex flex-col items-center justify-center gap-1 py-3 px-1 text-xs font-medium text-base-content/70 hover:text-primary hover:bg-base-200 transition-colors"
          data-testid="quick-action-maintenance"
        >
          <Wrench className="w-5 h-5" />
          <span className="max-w-full truncate">{t('dashboard.quick_actions.bar.log_maintenance')}</span>
        </Link>

        {parcelTextExtractionEnabled && (
          <Link
            to="/parcels/scan"
            className="flex-1 basis-0 min-w-0 flex flex-col items-center justify-center gap-1 py-3 px-1 text-xs font-medium text-base-content/70 hover:text-primary hover:bg-base-200 transition-colors"
            data-testid="quick-action-scan"
          >
            <Camera className="w-5 h-5" />
            <span className="max-w-full truncate">{t('dashboard.quick_actions.bar.quick_add')}</span>
          </Link>
        )}
      </div>
    </nav>
  )
}
